import { tokenStore } from '@/lib/api/token-store';
import { env } from '@/lib/env';

import { createRealtimeClient, type RealtimeHandler } from './realtime-client';

/** How a subscription is actually established. Overridable so tests drive the injectable socket
 *  mock rather than a live server (spec § Testing Decisions seam 2). */
export type TelemetrySource = (handler: RealtimeHandler) => () => void;

let source: TelemetrySource | null = null;

/** Test seam. Pass `null` to restore the real socket path — do this in `afterEach`. */
export function setTelemetrySource(next: TelemetrySource | null): void {
  source = next;
}

/**
 * Subscribe to the typed `telemetry` channel (ADR-0015). Returns an unsubscribe function.
 *
 * **The channel is dark, and that is the specified state.** The backend has published no telemetry
 * payload contract (grilling F11), so `VITE_SOCKET_URL` is unset and this returns a no-op: nothing
 * connects, nothing is delivered, and the panel's values freeze at whatever `map-popup` returned —
 * which is exactly 113588 §9d's "freeze at the last received readings rather than clearing to
 * blank". Nothing here surfaces a connection state, which is §9d/§9e's "silently" and "no
 * notification of either the interruption or the recovery"; socket.io's own reconnect handles the
 * retry with no code of ours involved.
 *
 * ponytail: **one connection per subscription**, not a shared refcounted singleton. There is exactly
 * one subscriber in the app — the open quick view — and tearing the socket down on unsubscribe is
 * what makes §9f's "suspend while the tab is hidden" mean something rather than leaving an idle
 * connection per open tab (grilling Q6). Give this a refcounted singleton when a second channel
 * (`command-ack`, `notifications`) lands and two consumers would otherwise open two sockets.
 */
export function subscribeToTelemetry(handler: RealtimeHandler): () => void {
  if (source) return source(handler);
  if (!env.VITE_SOCKET_URL) return () => {};

  const client = createRealtimeClient(env.VITE_SOCKET_URL, {
    getToken: () => tokenStore.getAccessToken(),
  });
  const unsubscribe = client.subscribe('telemetry', handler);

  return () => {
    unsubscribe();
    client.disconnect();
  };
}
